import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Loader2, AlertTriangle, ShieldCheck, GraduationCap,
  Bus, Coffee, Building2, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ratingColor = {
  'VERY SAFE': 'bg-emerald-600',
  'SAFE': 'bg-green-600',
  'MODERATE': 'bg-amber-500',
  'ELEVATED RISK': 'bg-red-600',
  'EXCELLENT': 'bg-emerald-600',
  'GOOD': 'bg-green-600',
  'AVERAGE': 'bg-amber-500',
  'LIMITED': 'bg-red-600',
};

const NeighborhoodIntelligence = ({ property }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const location = property?.location?.fullAddress
        || `${property?.location?.area || ''}, ${property?.location?.county || ''}`;
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/neighborhood-intelligence`, {
        location,
        property_type: property?.propertyType || 'house',
      });
      setReport(data.report);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="neighborhood-intelligence">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#7042cc]" />
          Neighborhood Intelligence
        </CardTitle>
        <CardDescription>
          10+ hours of area research in one report — safety, schools, transport, amenities and future developments.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!report && !loading && (
          <Button
            onClick={run}
            data-testid="run-neighborhood-btn"
            className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
          >
            Generate neighborhood report
          </Button>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-gray-600" data-testid="neighborhood-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            Researching the area…
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {report && !report.error && (
          <div className="space-y-5" data-testid="neighborhood-report">
            <p className="text-sm text-gray-700 leading-relaxed" data-testid="neighborhood-summary">
              {report.summary}
            </p>

            {/* Score grid */}
            <div className="grid grid-cols-3 gap-2">
              <Score label="Lifestyle" value={report.lifestyle_score} testid="score-lifestyle" />
              <Score label="Investment" value={report.investment_score} testid="score-investment" />
              <Score label="Family" value={report.family_score} testid="score-family" />
            </div>

            {/* Safety */}
            <Section icon={<ShieldCheck className="h-4 w-4 text-emerald-600" />} title="Safety">
              <Rating
                rating={report.safety?.rating}
                comment={report.safety?.comment}
                testid="neighborhood-safety"
              />
            </Section>

            {/* Schools */}
            <Section icon={<GraduationCap className="h-4 w-4 text-[#7042cc]" />} title="Schools">
              <Rating
                rating={report.schools?.rating}
                comment={report.schools?.comment}
                testid="neighborhood-schools"
              />
              {report.schools?.primary_examples?.length > 0 && (
                <div className="text-xs text-gray-600 mt-1">
                  <span className="font-medium">Primary:</span> {report.schools.primary_examples.join(', ')}
                </div>
              )}
              {report.schools?.secondary_examples?.length > 0 && (
                <div className="text-xs text-gray-600">
                  <span className="font-medium">Secondary:</span> {report.schools.secondary_examples.join(', ')}
                </div>
              )}
            </Section>

            {/* Transport */}
            <Section icon={<Bus className="h-4 w-4 text-[#7042cc]" />} title="Transport">
              <Rating
                rating={report.transport?.rating}
                comment={report.transport?.comment}
                testid="neighborhood-transport"
              />
              <div className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Commute to city centre:</span>{' '}
                <span data-testid="neighborhood-commute-min">
                  {report.transport?.commute_to_city_centre_min} min
                </span>
              </div>
              {report.transport?.options?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {report.transport.options.map((opt, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">
                      {opt}
                    </Badge>
                  ))}
                </div>
              )}
            </Section>

            {/* Amenities */}
            {report.amenities?.length > 0 && (
              <Section icon={<Coffee className="h-4 w-4 text-[#7042cc]" />} title="Local amenities">
                <ul className="space-y-1">
                  {report.amenities.map((a, i) => (
                    <li key={i} className="text-sm text-gray-700">• {a}</li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Future developments */}
            {report.future_developments?.length > 0 && (
              <Section icon={<Building2 className="h-4 w-4 text-[#7042cc]" />} title="Future developments">
                <ul className="space-y-1">
                  {report.future_developments.map((d, i) => (
                    <li key={i} className="text-sm text-gray-700">• {d}</li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Best for */}
            {report.best_for?.length > 0 && (
              <Section icon={<Users className="h-4 w-4 text-[#7042cc]" />} title="Best for">
                <div className="flex flex-wrap gap-1.5">
                  {report.best_for.map((b, i) => (
                    <Badge key={i} className="bg-[#F5EFFF] text-[#5b35a8] hover:bg-[#F5EFFF] border-[#7042cc]/20">
                      {b}
                    </Badge>
                  ))}
                </div>
              </Section>
            )}

            {report.demographic_profile && (
              <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                <span className="font-medium">Resident profile: </span>{report.demographic_profile}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Score = ({ label, value, testid }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3 text-center">
    <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</div>
    <div className="text-xl font-semibold text-gray-900" data-testid={testid}>
      {value}<span className="text-sm text-gray-400">/10</span>
    </div>
  </div>
);

const Section = ({ icon, title, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5">
      {icon}
      <span className="text-sm font-semibold text-gray-900">{title}</span>
    </div>
    <div className="pl-5">{children}</div>
  </div>
);

const Rating = ({ rating, comment, testid }) => (
  <div>
    <div className="flex items-center gap-2 mb-1">
      <Badge
        className={`${ratingColor[rating] || 'bg-gray-600'} text-white text-[10px] px-2 py-0.5`}
        data-testid={testid}
      >
        {rating || 'UNKNOWN'}
      </Badge>
    </div>
    {comment && <div className="text-xs text-gray-600">{comment}</div>}
  </div>
);

export default NeighborhoodIntelligence;
